/**
 * Developers — every funder's interface behind one call.
 *
 * Replaces /solutions/developers ("Technical Specifications"), which spoke to
 * three readers at once and promised "an honest account of which parts are
 * live" without giving one. The security half moved to /security. What is
 * left is for an engineer at a partner: the surface, how to authenticate, the
 * webhooks, and the same six capabilities as Platform with a status on each.
 *
 * Only shipped endpoints appear in examples. The quickstart calls the Partner
 * API v1 surface (app/routers/partner_doula.py); the webhook list names only
 * the events that have a sender today.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Download, KeyRound, Webhook } from 'lucide-react';
import usePageMeta from '@/hooks/usePageMeta';
import { SEO_ROUTES } from '@/src/seo/routes';
import MarketingHeader from '@/components/marketing/MarketingHeader';
import MarketingFooter from '@/components/marketing/MarketingFooter';
import CapabilitySpine from '@/components/marketing/CapabilitySpine';
import { contactHref } from '@/components/marketing/siteFacts';

const BASE_URL = 'https://api.weavein.ai';

const QUICKSTART = `# Check a member's coverage before you refer them
curl -s "${BASE_URL}/api/partner/v1/members/{member_id}/eligibility" \\
 -H "X-API-Key: $WEAVEIN_API_KEY"

# Create a referral. Consent is a precondition: without it nothing is stored.
curl -s -X POST "${BASE_URL}/api/partner/v1/referrals" \\
 -H "X-API-Key: $WEAVEIN_API_KEY" \\
 -H "Content-Type: application/json" \\
 -d '{"member_id": 123, "source_type": "community_partner", "consent_given": true}'

# Follow the money: encounters delivered, and what was paid out for them
curl -s "${BASE_URL}/api/partner/v1/members/{member_id}/encounters" -H "X-API-Key: $WEAVEIN_API_KEY"
curl -s "${BASE_URL}/api/partner/v1/members/{member_id}/disbursements" -H "X-API-Key: $WEAVEIN_API_KEY"`;

const ENDPOINTS = [
  ['GET', '/api/partner/v1/health', 'Key and connectivity check'],
  ['POST', '/api/partner/v1/members', 'Create or match a member in your organization'],
  ['GET', '/api/partner/v1/members/{id}/eligibility', 'Coverage, checked with the payer'],
  ['POST', '/api/partner/v1/referrals', 'Refer a member, with consent recorded first'],
  ['GET', '/api/partner/v1/members/{id}/encounters', 'What was delivered, by whom, when'],
  ['GET', '/api/partner/v1/members/{id}/disbursements', 'Payments tied to the milestone that earned them'],
  ['GET', '/api/partner/v1/members/{id}/disputes', 'Payment disputes and their state'],
];

const WEBHOOKS = [
  ['doula.request.accepted', 'A provider accepted a request you made for a member'],
  ['disbursement.status_changed', 'A payout moved: scheduled, sent, paid or failed'],
];

const DOWNLOADS = [
  { href: '/weavein-partner-doula.postman_collection.json', label: 'Partner API v1 — Postman collection' },
  { href: '/weavein-api.postman_collection.json', label: 'Platform API — Postman collection' },
  { href: '/weavein-sandbox.postman_environment.json', label: 'Sandbox environment' },
  { href: '/weavein-production.postman_environment.json', label: 'Production environment' },
];

export default function DevelopersPage() {
  usePageMeta(SEO_ROUTES['/developers']);

  return (
    <div className="bg-white dark:bg-gray-950">
      <MarketingHeader />

      <section className="bg-paper border-b border-v2border text-ink">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <p className="text-sm font-semibold tracking-widest text-v2accent uppercase mb-4">Developers</p>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Every funder&rsquo;s interface<br />
            <span className="text-v2accent">behind one call.</span>
          </h1>
          <p className="text-xl text-ink-500 max-w-3xl mb-8">
            The Plaid for the healthcare safety net: eligibility, referrals, encounters, disbursement and remittance for
            Medicaid, Medicare, county and grant funders, through one API and an agent you can embed in your own product.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link
              to={contactHref('sandbox')}
              className="inline-flex items-center px-8 py-4 bg-ink text-paper text-lg font-semibold rounded-lg hover:bg-ink-700 transition-colors"
            >
              Request sandbox access
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
            <a
              href="#quickstart"
              className="inline-flex items-center px-8 py-4 border-2 border-v2border text-ink text-lg font-semibold rounded-lg hover:bg-ink hover:text-paper transition-colors"
            >
              Quickstart
            </a>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white dark:bg-gray-950" id="quickstart">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-ink mb-3">Quickstart</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6 max-w-3xl">
            Base URL <code className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-sm">{BASE_URL}</code>. Every
            request carries an <code className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-sm">X-API-Key</code>{' '}
            header.
          </p>
          <pre className="bg-v2accent text-gray-100 rounded-xl p-6 overflow-x-auto text-sm leading-relaxed">
            <code>{QUICKSTART}</code>
          </pre>
        </div>
      </section>

      <section className="py-16 bg-gray-50 dark:bg-gray-900 border-y border-gray-200 dark:border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">Partner API v1</h2>
            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950">
              <table className="w-full text-sm">
                <tbody>
                  {ENDPOINTS.map(([m, path, what]) => (
                    <tr key={path} className="border-b last:border-0 border-gray-100 dark:border-gray-800">
                      <td className="p-3 font-mono text-xs font-semibold text-v2accent">{m}</td>
                      <td className="p-3 font-mono text-xs text-gray-800 dark:text-gray-100">{path}</td>
                      <td className="p-3 text-gray-600 dark:text-gray-300">{what}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="space-y-8">
            <div>
              <h2 className="flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-gray-100 mb-3">
                <KeyRound className="w-5 h-5 text-v2accent" aria-hidden="true" />
                Keys pin the organization
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Each key belongs to one organization and carries scopes. The key decides whose members you can see; an
                organization header cannot widen it. Keys are stored only as SHA-256 hashes, so a lost key is rotated, never
                recovered.
              </p>
            </div>
            <div>
              <h2 className="flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-gray-100 mb-3">
                <Webhook className="w-5 h-5 text-v2accent" aria-hidden="true" />
                Signed webhooks
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                HMAC-SHA256 in <code className="text-xs">X-WeaveIn-Signature</code>, three retries, idempotent on{' '}
                <code className="text-xs">webhook_id</code>. Events with a sender today:
              </p>
              <ul className="space-y-2 text-sm">
                {WEBHOOKS.map(([e, what]) => (
                  <li key={e}>
                    <code className="text-xs font-semibold text-v2accent">{e}</code>
                    <span className="text-gray-600 dark:text-gray-300"> — {what}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-gray-100 mb-3">
                <Download className="w-5 h-5 text-v2accent" aria-hidden="true" />
                Postman
              </h2>
              <ul className="space-y-1.5 text-sm">
                {DOWNLOADS.map((d) => (
                  <li key={d.href}>
                    <a href={d.href} download className="text-ink hover:underline">
                      {d.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <CapabilitySpine
        mode="spec"
        heading="The same six capabilities, in API terms"
        lede="Status is what runs in production today. Live means in use; Pilot means built and running with a first customer."
        showStatus
      />

      <section className="py-12 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
          <div>
            <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-2">Standards</h3>
            <p className="text-gray-600 dark:text-gray-300">FHIR R4, HL7 v2 ADT, X12 270/271, 276/277, 278, 837P and 835.</p>
          </div>
          <div>
            <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-2">Funder coverage</h3>
            <p className="text-gray-600 dark:text-gray-300">
              Which payers are mapped and reachable:{' '}
              <Link to="/funder-coverage" className="text-ink hover:underline">Funder Coverage</Link>.
            </p>
          </div>
          <div>
            <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-2">Security review</h3>
            <p className="text-gray-600 dark:text-gray-300">
              HIPAA, BAAs, SSO and isolation:{' '}
              <Link to="/security" className="text-ink hover:underline">Security</Link>.
            </p>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
